import React from 'react';
import { PlayerWaveform, PlayerWaveformTheme } from '@landr/react-player';
import useDuration from 'hooks/useDuration';
import { Sample } from 'types';
import styled from 'styled-components';
import { Text, palette } from '@landr/maestro';
import { useWaveformData } from 'hooks/useWaveformData';
import {
    WaveformWrapper,
    WaveformContainer,
} from 'components/CommonUi/WaveformWrapper';

export interface SamplePlayerProps {
    sample: Sample;
    theme?: 'white' | 'black';
}

// NOTE: It's not possible to access the palette directly to pass the value into the theme.
const waveformThemeLight: PlayerWaveformTheme = {
    original: {
        barColor: 'rgba(255,255,255,0.48)',
        barProgressColor: 'rgba(50,203,203,1)',
        barProgressColorAfterPosition: 'rgba(255,255,255,0.74)',
        barHoverColor: 'rgba(255,255,255,0.74)',
        animInSpeed: 0,
    },
};

const waveformThemeDark: PlayerWaveformTheme = {
    original: {
        barColor: 'rgba(0,0,0,0.48)',
        barProgressColor: 'rgba(0,0,0,1)',
        barProgressColorAfterPosition: 'rgba(0,0,0,0.74)',
        barHoverColor: 'rgba(0,0,0,0.74)',
        animInSpeed: 0,
    },
};

const TextStyled = styled(Text)<{
    isThemeWhite: boolean;
    duration: string | null;
}>`
    color: ${({ isThemeWhite }): ReturnType<typeof palette> =>
        isThemeWhite ? palette('neutral.0') : palette('neutral.1000', 0.96)};
    visibility: ${({ duration }): string => (duration ? 'visible' : 'hidden')};
`;

const PlayerWaveformConfig = {
    mirrored: true,
    playOnSeek: false,
    silenceBarHeight: 1,
};

const SampleWaveformPlayer: React.FC<SamplePlayerProps> = ({
    sample,
    theme = 'white',
}) => {
    const isThemeWhite = theme && theme === 'white' ? true : false;
    const { dataPoints } = useWaveformData(sample);
    const duration = useDuration(sample.duration);

    return (
        <WaveformWrapper>
            <WaveformContainer>
                <PlayerWaveform
                    theme={
                        isThemeWhite ? waveformThemeLight : waveformThemeDark
                    }
                    playerId={sample.guid}
                    dataPoints={dataPoints}
                    config={PlayerWaveformConfig}
                    isResizable={true}
                />
            </WaveformContainer>
            <TextStyled
                size="xs"
                my="auto"
                isThemeWhite={isThemeWhite}
                duration={duration}
            >
                {/* set a default text (hidden) so waveform doesn't overlaps it when duration is updated */}
                {duration || '0:00'}
            </TextStyled>
        </WaveformWrapper>
    );
};

export default SampleWaveformPlayer;
